import { useEffect, useMemo, useState } from 'preact/hooks';
import { navigate, useRoute } from '../lib/router';
import { keep } from '../lib/remember';
import { useNotice } from '../shell/notice';
import { useWide } from '../shell/hooks';
import { bookLabel, statusName } from '../book/model';
import { detailsFor, searchFor, type Found } from '../add/add';
import { loadDetails, type Details } from '../shelves/load';
import { num, titleKey } from './model';
import { ListFoot } from '../ui/ListFoot';
import { SettingMenu } from '../ui/PillMenu';
import { BookCell, ListRow, ListTable, type Col } from '../ui/ListTable';
import { flipSort, useTopLine } from './parts';
import { sortList } from './review';
import {
  candidateKey,
  canUse,
  coverLink,
  differenceText,
  keepMineValue,
  noneOfTheseValue,
  storeIdIn,
  typedValue,
  LOOKUP_CHIPS,
  linkStoreId,
  type Candidate,
  type Dismissal,
  type LookupChip,
  type LookupItem,
} from './lookups';
import { copyFor, coverEdition, dismiss, edit, fillOne, pickMatch, undoEdit, undoPick } from './lookupLoad';

// Review & update books, step 2 (Kristi, Oct 30; mocked up in chat): one quick list per lookup
// chip, 20 at a time, Currently Reading and Up Next first. Each answer saves at once with Undo; a
// handled row stays in place with ✓ until you leave. Answering doesn't mark the book Reviewed.

const INTRO: Record<LookupChip, string> = {
  match: 'Lookups found several books that could be this one. Pick it and its details fill in now.',
  not_found: 'Not found on Open Library or Google Books, or the ISBN belongs to another book.',
  summary: 'No summary on Open Library or Google Books. Paste the publisher’s description, or leave it blank.',
  cover: 'No cover on Open Library or Google Books. Paste a link to a picture (a Google Drive share link works); only you see it.',
  kindle: 'No Kindle or Audible ID yet. Paste the store link or the ID, or leave it blank.',
  different: 'Lookups found a value different from yours. Yours stays until you choose.',
};

const SHARED = 'A shared fact: as the admin, this changes it for everyone.';

type Done = { label: string; undo: () => Promise<void> };

export function LookupList({
  chip,
  items,
  dismissals,
  setDismissals,
  onAnswered,
  onUndone,
}: {
  chip: LookupChip;
  items: LookupItem[];
  dismissals: Dismissal[];
  setDismissals: (f: (d: Dismissal[]) => Dismissal[]) => void;
  /** An item is answered: Review leaves it in place (✓) until you leave, but it no longer counts. */
  onAnswered: (key: string) => void;
  onUndone: (key: string) => Promise<void>;
}) {
  const notice = useNotice();
  const wide = useWide();
  const topRef = useTopLine();
  const { path } = useRoute();
  const [shown, setShown] = useState(20);
  const [done, setDone] = useState<Record<string, Done>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const name = LOOKUP_CHIPS.find((c) => c.key === chip)!.name;
  // The author as entered in the app (Kristi, Oct 30), read for the rows showing.
  const [details, setDetails] = useState<Map<string, Details>>(new Map());
  const visibleBooks = items.slice(0, shown).map((x) => x.book);
  useEffect(() => {
    const missing = visibleBooks.filter((b) => !details.has(b.id));
    if (!missing.length) return;
    let live = true;
    loadDetails(missing, new Set())
      .then((m) => live && setDetails((old) => new Map([...old, ...m])))
      .catch(() => null);
    return () => {
      live = false;
    };
  }, [visibleBooks.map((b) => b.id).join(',')]);
  const authorOf = (it: LookupItem) => details.get(it.book.id)?.author || it.book.author || '';

  const finish = (it: LookupItem, label: string, undo: () => Promise<void>, message: string) => {
    setDone((d) => ({ ...d, [it.key]: { label, undo } }));
    onAnswered(it.key);
    notice({ message, undo: () => void takeBack(it, undo) });
  };
  const takeBack = async (it: LookupItem, undo: () => Promise<void>) => {
    try {
      await undo();
      setDone((d) => {
        const m = { ...d };
        delete m[it.key];
        return m;
      });
      await onUndone(it.key);
      notice({ message: `Undone: ${it.book.title} is back in ${name}` });
    } catch (e) {
      notice({ message: `Couldn’t undo: ${e instanceof Error ? e.message : String(e)}`, done: false });
    }
  };
  // A save that fails says why on its own row too, not only in the notice (Kristi, Oct 30: "nothing
  // when I click save").
  const [failed, setFailed] = useState<Record<string, string>>({});
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const run = async (it: LookupItem, f: () => Promise<void>) => {
    setBusy(it.key);
    setFailed((x) => ({ ...x, [it.key]: '' }));
    try {
      await f();
    } catch (e) {
      const why = e instanceof Error ? e.message : String(e);
      setFailed((x) => ({ ...x, [it.key]: `Not saved: ${why}` }));
      notice({ message: `Not saved: ${why}`, done: false });
    }
    setBusy(null);
  };

  /** Remembers a "stop asking" answer, with Undo. */
  const remember = (it: LookupItem, kind: Dismissal['kind'], value: string | null, label: string) =>
    run(it, async () => {
      const back = await dismiss(it.book.id, it.field, kind, value);
      setDismissals((ds) => [...ds.filter((d) => !(d.bookId === it.book.id && d.field === it.field)), { id: '', bookId: it.book.id, field: it.field, kind, foundValue: value }]);
      finish(it, label, async () => {
        await back();
        setDismissals((ds) => ds.filter((d) => !(d.bookId === it.book.id && d.field === it.field)));
      }, `${it.book.title}: ${label}`);
    });

  const pick = (it: LookupItem, c: { openLibraryId?: string | null; googleBooksId?: string | null; title: string; year: number | null }) =>
    run(it, async () => {
      const r = await pickMatch(it.book.id, it.book.workId, c);
      const label = r.tonight
        ? `✓ Picked · ${r.filled ? `${num(r.filled)} filled now, the rest` : 'details fill in'} tonight`
        : `✓ Picked · ${r.filled ? `${num(r.filled)} ${r.filled === 1 ? 'detail' : 'details'} filled` : 'nothing new to fill'}`;
      finish(it, label, () => undoPick(r.run), `${it.book.title}: picked ${c.title}${c.year ? ` (${c.year})` : ''}`);
    });

  const openPage = (it: LookupItem) => {
    if (!wide) keep('queue:review:lookup:return', it.key);
    navigate(`/book/${bookLabel(it.book.label)}`, { state: { from: path }, keepScroll: wide });
  };

  // Sorted by its headers (R6, Kristi Nov 2), opening as today: Currently Reading and Up Next first
  // (the Shelf column, the order the items come in).
  const [sort, setSort] = useState<{ key: LookupSortKey; reversed: boolean }>({ key: 'shelf', reversed: false });
  const sorted = useMemo(() => {
    const at = new Map(items.map((x, i) => [x.key, i]));
    return sortList(items, (x) => (sort.key === 'title' ? titleKey(x.book.title) : at.get(x.key)!), sort.reversed);
  }, [items, sort]);
  const visible = sorted.slice(0, shown);
  const waiting = items.filter((x) => !done[x.key]).length;
  return (
    <div class="rw-list rw-fixlist lk-list" data-chip={chip}>
      <div class="rw-fix-top" ref={topRef}>
        <span class="rw-box-name">{name}</span>
        <span class="rw-quiet">
          · {num(waiting)} {waiting === 1 ? 'book' : 'books'} waiting
        </span>
        <span class="rw-grow" />
        {/* A phone has no header row: its sort is the Sort pill (R6 T8), in step with the headers. */}
        {!wide ? (
          <SettingMenu<LookupSortKey>
            name="Sort"
            choices={[['shelf', 'Up Next first'], ['title', 'Title']]}
            value={sort.key}
            onChange={(k) => setSort({ key: k, reversed: false })}
          />
        ) : null}
        {/* ↑ Top is in My books' pinned bar (the shared piece). */}
      </div>
      <p class="rw-quiet rw-fix-line">{INTRO[chip]}</p>
      <ListTable<LookupSortKey>
        label={name}
        cols={LOOKUP_COLS}
        sort={{ key: sort.key, desc: sort.reversed }}
        onSort={(k) => setSort(flipSort(sort, k))}
        stickyTop="calc(var(--top-bar-h, 0px) + var(--queue-top-h, 0px) + var(--rw-top-h, 0px))"
        empty="Nothing waiting here."
        foot={<ListFoot shown={Math.min(shown, items.length)} total={items.length} page={20} onMore={() => setShown(shown + 20)} onAll={() => setShown(items.length)} onFewer={() => setShown(20)} />}
      >
        {visible.map((it) => {
          const book = <BookCell title={it.book.title} cover={details.get(it.book.id)?.cover ?? null} author={<span class="lk-author">{authorOf(it) || 'No author entered'}</span>} onOpen={() => openPage(it)} />;
          const shelf = <span class="rw-quiet lk-shelf">{statusName(it.book.status)}</span>;
          const answer =
            skipped.has(it.key) && !done[it.key] ? (
              <span class="lk-answer">
                <span class="rw-quiet">Skipped for now · it stays here, and the nightly look-up keeps trying</span>
                <button
                  type="button"
                  class="btn-link"
                  onClick={() =>
                    setSkipped((x) => {
                      const n = new Set(x);
                      n.delete(it.key);
                      return n;
                    })
                  }
                >
                  Back to it
                </button>
              </span>
            ) : done[it.key] ? (
              <span class="lk-answer">
                <span class="rw-fix-status">{done[it.key]!.label}</span>
                <button type="button" class="btn-link" onClick={() => void takeBack(it, done[it.key]!.undo)}>
                  Undo
                </button>
              </span>
            ) : (
              <span class="lk-qa">
                <Answer it={it} author={authorOf(it)} failed={failed[it.key] ?? ''} skip={() => setSkipped((x) => new Set([...x, it.key]))} busy={busy === it.key} pick={pick} remember={remember} run={run} finish={finish} dismissals={dismissals} />
              </span>
            );
          return (
            <ListRow
              key={it.key}
              class={`rw-fix-row lk-row${done[it.key] ? ' lk-done' : ''}`}
              data-row={it.key}
              quiet={Boolean(done[it.key])}
              cells={[book, shelf, answer]}
              phone={{
                book: <BookCell title={it.book.title} cover={details.get(it.book.id)?.cover ?? null} author={<span class="lk-author">{authorOf(it) || 'No author entered'}</span>} onOpen={() => openPage(it)} />,
                control: shelf,
                line: answer,
              }}
            />
          );
        })}
      </ListTable>
    </div>
  );
}

type LookupSortKey = 'title' | 'shelf';
// Book · Shelf · Question (the question, then its answers; the buttons need no header, R6 T3).
const LOOKUP_COLS: Col<LookupSortKey>[] = [
  { label: 'Book', width: 'minmax(0, 1fr)', sort: 'title', book: true },
  { label: 'Shelf', width: '120px', sort: 'shelf' },
  { label: 'Question', width: 'minmax(0, 2.6fr)' },
];

type Finish = (it: LookupItem, label: string, undo: () => Promise<void>, message: string) => void;

/** The row's question and its answers, by chip. */
function Answer({
  it,
  author,
  failed,
  skip,
  busy,
  pick,
  remember,
  run,
  finish,
  dismissals,
}: {
  it: LookupItem;
  /** The author as entered in the app: searched for with the title. */
  author: string;
  /** Why the last save on this row failed, if it did. */
  failed: string;
  /** Skip for now: the row steps aside for this visit; nothing is saved (Kristi, Oct 30). */
  skip: () => void;
  busy: boolean;
  pick: (it: LookupItem, c: { openLibraryId?: string | null; googleBooksId?: string | null; title: string; year: number | null }) => Promise<void>;
  remember: (it: LookupItem, kind: Dismissal['kind'], value: string | null, label: string) => Promise<void>;
  run: (it: LookupItem, f: () => Promise<void>) => Promise<void>;
  finish: Finish;
  dismissals: Dismissal[];
}) {
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [searching, setSearching] = useState(false);
  const [found, setFound] = useState<Found[] | null>(null);
  // The search starts with the title and the author as entered (Kristi, Oct 30: title alone missed).
  // Untouched, the box shows the title and author (filled in once the author is read); once you type, your words.
  const [typedWords, setWords] = useState<string | null>(null);
  const words = typedWords ?? `${it.book.title} ${author}`.trim();
  const saveIsbn = () =>
    run(it, async () => {
      const copy = await copyFor(it.book.id, it.difference?.table === 'editions' ? { editionId: it.difference.rowId } : { withIsbn: true });
      if (!copy) throw new Error('No copy of yours to put the ISBN on.');
      const action = await edit('copy_edit', { copy, field: 'isbn', value: text.trim() });
      finish(it, '✓ ISBN saved', async () => void (action && (await undoEdit(action))), `${it.book.title}: ISBN saved`);
    });
  const isbnBox = (
    <>
      <div class="lk-inline">
        <input class="lk-input" value={text} placeholder="The right ISBN" aria-label="The right ISBN" onInput={(e) => setText((e.target as HTMLInputElement).value)} />
        <button type="button" class="btn-main" disabled={busy || !text.trim()} onClick={() => void saveIsbn()}>
          Save
        </button>
      </div>
      <span class="rw-quiet">{SHARED}</span>
    </>
  );

  // A pasted Amazon or Audible link that finds nothing still gives its ID (Kristi, Oct 30).
  const [linkId, setLinkId] = useState<{ field: 'asin' | 'audible_id'; id: string } | null>(null);
  const search = async () => {
    if (!words.trim()) return setError('Type a title, an ISBN or a link first.');
    setError('');
    setLinkId(null);
    setSearching(true);
    try {
      const a = await searchFor(words.trim());
      setFound(a.results);
      if (!a.results.length && linkStoreId(words)) setLinkId(linkStoreId(words));
      else if (!a.results.length)
        setError(
          it.chip === 'not_found'
            ? 'Nothing found for these words. Try an ISBN or an Amazon or Goodreads link, keep your entry, or skip it for now.'
            : 'Nothing found for these words. Try an ISBN or an Amazon or Goodreads link.',
        );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setSearching(false);
  };

  const candidateRow = (c: Candidate | Found) => (
    <div key={'key' in c && typeof c.key === 'string' ? c.key : candidateKey(c as Candidate)} class="lk-cand">
      <span class="lk-cand-text">
        <span>{c.title}</span>
        <span class="rw-quiet">
          {[c.authors.join(', '), c.year, c.editions ? `${num(c.editions)} ${c.editions === 1 ? 'edition' : 'editions'}` : null, 'languages' in c && c.languages?.length ? c.languages.join(', ') : null, c.source]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
      <a class="btn-link" href={c.url} target="_blank" rel="noopener noreferrer">
        {c.source === 'Open Library' ? 'Open Library ↗' : 'Google Books ↗'}
      </a>
      <button type="button" class="btn-secondary" disabled={busy} onClick={() => void pick(it, c)}>
        This one
      </button>
    </div>
  );

  const searchBox = (
    <div class="lk-search">
      <div class="lk-inline">
        <input class="lk-input" value={words} aria-label="Search for this book" onInput={(e) => setWords((e.target as HTMLInputElement).value)} onKeyDown={(e) => e.key === 'Enter' && void search()} />
        <button type="button" class="btn-main" disabled={searching || busy} onClick={() => void search()}>
          {searching ? 'Searching…' : 'Search'}
        </button>
      </div>
      <span class="rw-quiet">
        Change the words, or paste an ISBN or an Amazon, Audible or Goodreads link.{' '}
        <a class="btn-link" href={`https://www.amazon.com/s?k=${encodeURIComponent(`${it.book.title} ${author}`.trim())}&i=stripbooks`} target="_blank" rel="noopener noreferrer">
          Search Amazon ↗
        </a>{' '}
        to find its ISBN or its page's link.
      </span>
      {found?.length ? <div class="lk-cands">{found.map((f) => candidateRow(f))}</div> : null}
    </div>
  );

  const keepWithId = (x: { field: 'asin' | 'audible_id'; id: string }) =>
    run(it, async () => {
      const copy = await copyFor(it.book.id, { format: x.field === 'audible_id' ? 'audible' : 'kindle' });
      if (!copy) throw new Error(`No ${x.field === 'audible_id' ? 'Audible' : 'Kindle'} copy to put the ID on.`);
      const action = await edit('copy_edit', { copy, field: x.field, value: x.id });
      const forget = await dismiss(it.book.id, it.field, 'keep_as_typed', typedValue(it.book));
      finish(
        it,
        `✓ ${x.field === 'audible_id' ? 'Audible' : 'Kindle'} ID saved · kept your entry`,
        async () => {
          if (action) await undoEdit(action);
          await forget();
        },
        `${it.book.title}: ${x.field === 'audible_id' ? 'Audible' : 'Kindle'} ID ${x.id} saved`,
      );
    });
  const linkFound = linkId ? (
    <div class="heads-up lk-linkid" role="group" aria-label="The ID from your link">
      <span>
        Open Library and Google Books don’t have this book, but your link gives its {linkId.field === 'audible_id' ? 'Audible' : 'Kindle'} ID, <b>{linkId.id}</b>.
      </span>
      <button type="button" class="btn-main" disabled={busy} onClick={() => void keepWithId(linkId)}>
        Save the ID and keep my entry
      </button>
    </div>
  ) : null;
  const err = error || failed ? <span class="lk-error" role="alert">{error || failed}</span> : null;

  if (it.chip === 'match') {
    return (
      <>
        <span class="lk-main">
          {found === null && it.candidates?.length ? (
            <div class="lk-cands">{it.candidates.map((c) => candidateRow(c))}</div>
          ) : (
            <>
              {!it.candidates?.length ? <span class="rw-quiet">Its possible matches come with its next nightly look-up. Search now to pick one.</span> : null}
              {searchBox}
            </>
          )}
          {err}
        </span>
        <span class="lk-actions">
          <button type="button" class="btn-secondary" disabled={busy} onClick={() => void remember(it, 'none_of_these', noneOfTheseValue(it.candidates ?? []), '✓ None of these · asks again if a new match turns up')}>
            None of these
          </button>
          {found === null && it.candidates?.length ? (
            <button type="button" class="btn-link" onClick={() => setFound([])}>
              Search again…
            </button>
          ) : null}
          <button type="button" class="btn-link" disabled={busy} onClick={skip}>
            Skip for now
          </button>
        </span>
      </>
    );
  }

  if (it.chip === 'not_found' && it.field === 'match') {
    return (
      <>
        <span class="lk-main">
          {searchBox}
          {linkFound}
          {err}
        </span>
        <span class="lk-actions">
          <button type="button" class="btn-secondary" disabled={busy} onClick={() => void remember(it, 'keep_as_typed', typedValue(it.book), '✓ Kept your entry')}>
            Keep my entry
          </button>
          <span class="rw-quiet lk-explain">
            Search looks again with your words. Keep my entry keeps the book as you entered it and stops asking (it asks again if you change the title). Skip for now leaves it for later.
          </span>
          <button type="button" class="btn-link" disabled={busy} onClick={skip}>
            Skip for now
          </button>
        </span>
      </>
    );
  }

  if (it.chip === 'not_found' && it.field === 'isbn') {
    return (
      <>
        <span class="lk-main">
          <span>The ISBN on your copy belongs to another book on Open Library.</span>
          {isbnBox}
        </span>
        <span class="lk-actions">
          <button type="button" class="btn-secondary" disabled={busy} onClick={() => void remember(it, 'keep_as_typed', null, '✓ Kept as it is')}>
            Keep as it is
          </button>
          <button type="button" class="btn-link" disabled={busy} onClick={skip}>
            Skip for now
          </button>
        </span>
      </>
    );
  }

  if (it.chip === 'summary' || it.chip === 'cover' || it.chip === 'kindle') {
    const save = () =>
      run(it, async () => {
        const v = text.trim();
        if (!v) return setError('Paste something first.');
        let action: string | null;
        if (it.chip === 'summary') action = await edit('book_edit', { book: it.book.id, field: 'summary', value: v });
        else if (it.chip === 'cover') {
          const link = coverLink(v);
          if (!/^https:\/\//.test(link)) return setError('A cover is a link starting with https://');
          action = await edit('book_edit', { book: it.book.id, field: 'cover', value: link });
        } else {
          const id = storeIdIn(v);
          if (!id) return setError('That isn’t a store link or a 10-letter ID.');
          const copy = await copyFor(it.book.id, { format: it.field === 'audible_id' ? 'audible' : 'kindle' });
          if (!copy) throw new Error(`No ${it.field === 'audible_id' ? 'Audible' : 'Kindle'} copy to put it on.`);
          action = await edit('copy_edit', { copy, field: it.field, value: id });
        }
        const what = it.chip === 'summary' ? 'Summary' : it.chip === 'cover' ? 'Cover' : it.field === 'audible_id' ? 'Audible ID' : 'Kindle ID';
        finish(it, `✓ ${what} saved`, async () => void (action && (await undoEdit(action))), `${it.book.title}: ${what.toLowerCase()} saved`);
      });
    const label = it.chip === 'summary' ? 'Paste a summary' : it.chip === 'cover' ? 'Paste a cover link' : it.field === 'audible_id' ? 'Audible link or ID' : 'Kindle link or ID';
    const q = encodeURIComponent(`${it.book.title} ${author}`.trim());
    const store =
      it.chip === 'kindle'
        ? it.field === 'audible_id'
          ? { name: 'Search Audible ↗', url: `https://www.audible.com/search?keywords=${q}`, how: 'Open the audiobook on Audible, copy the page’s address and paste it here; the ID is the B0… at the end of the address.' }
          : { name: 'Search Amazon ↗', url: `https://www.amazon.com/s?k=${q}&i=digital-text`, how: 'Open the Kindle edition on Amazon, copy the page’s address and paste it here; the ID is the B0… after /dp/.' }
        : null;
    return (
      <>
        <span class="lk-main">
          {/* No summary and No cover: the search comes first, as on Pick the match (Kristi, Oct 30); pasting is the other way. */}
          {(it.chip === 'summary' || it.chip === 'cover') && it.row ? <FindOne it={it} author={author} busy={busy} run={run} finish={finish} /> : null}
          {it.chip !== 'kindle' ? <span class="lk-or">{it.chip === 'summary' ? 'Or paste the publisher’s description:' : 'Or paste a link to a picture:'}</span> : null}
          <div class="lk-inline lk-paste">
            {it.chip === 'summary' ? (
              <textarea class="lk-input" rows={2} value={text} aria-label={label} placeholder="Paste the publisher’s description" onInput={(e) => setText((e.target as HTMLTextAreaElement).value)} />
            ) : (
              <input
                class="lk-input"
                value={text}
                aria-label={label}
                placeholder={it.chip === 'cover' ? 'https://…' : it.field === 'audible_id' ? 'audible.com/pd/… or B0…' : 'amazon.com/dp/… or B0…'}
                onInput={(e) => setText((e.target as HTMLInputElement).value)}
              />
            )}
            <button type="button" class="btn-main" disabled={busy} onClick={() => void save()}>
              Save
            </button>
          </div>
          {it.chip === 'cover' ? <span class="rw-quiet">Private: only you see your cover link. A Google Drive share link works.</span> : <span class="rw-quiet">{SHARED}</span>}
          {store ? (
            <span class="rw-quiet lk-explain">
              {/* Leaving the app: a book action with ↗ (R5). */}
              <a class="btn-book" href={store.url} target="_blank" rel="noopener noreferrer">
                {store.name}
              </a>{' '}
              {store.how}
            </span>
          ) : null}
          {err}
        </span>
        <span class="lk-actions">
          <button type="button" class="btn-secondary" disabled={busy} onClick={() => void remember(it, 'leave_blank', null, '✓ Left blank · won’t ask again')}>
            Leave it blank
          </button>
          <button type="button" class="btn-link" disabled={busy} onClick={skip}>
            Skip for now
          </button>
        </span>
      </>
    );
  }

  // Different from yours.
  const d = it.difference!;
  const t = differenceText(d);
  const before = dismissals.find((x) => x.bookId === it.book.id && x.field === it.field)?.foundValue ?? null;
  const use = () =>
    run(it, async () => {
      let action: string | null = null;
      if (d.field === 'first_published_on') {
        action = await edit('book_edit', { book: it.book.id, field: 'first_published', value: { on: `${d.found.slice(0, 4)}-01-01`, precision: 'year' } });
      } else if (d.field === 'fiction') {
        action = await edit('book_edit', { book: it.book.id, field: 'literary_genre', value: d.found.toLowerCase() });
      } else if (d.field === 'pages') {
        const copy = await copyFor(it.book.id, { editionId: d.rowId });
        if (!copy) throw new Error('You have no copy of that edition, so it can’t be changed from here.');
        action = await edit('copy_edit', { copy, field: 'edition_pages', value: Number(d.found) });
      }
      // The difference stays in the nightly record until the book's next look-up, so the answer is
      // remembered too (as Keep mine does: this found value is settled), or it came back on every
      // reload (Kristi, Oct 30). Undo takes back both.
      const forget = await dismiss(it.book.id, it.field, 'keep_mine', keepMineValue(d, before));
      finish(it, `✓ Using ${d.found}`, async () => {
        if (action) await undoEdit(action);
        await forget();
      }, `${it.book.title}: ${t.label.toLowerCase()} is now ${d.found}`);
    });
  return (
    <>
      <span class="lk-main">
        <span>
          {t.label}: <b>{t.yours}</b>
        </span>
        <span class="lk-diff">
          {d.source} {canUse(d) ? `says ${t.found}` : `: ${t.found}`}
        </span>
        {d.field === 'isbn13' ? isbnBox : <span class="rw-quiet">{SHARED}</span>}
      </span>
      <span class="lk-actions">
        {canUse(d) ? (
          <button type="button" class="btn-secondary" disabled={busy} onClick={() => void use()}>
            Use this
          </button>
        ) : null}
        <button type="button" class="btn-secondary" disabled={busy} onClick={() => void remember(it, 'keep_mine', keepMineValue(d, before), '✓ Kept yours')}>
          Keep mine
        </button>
        <button type="button" class="btn-link" disabled={busy} onClick={skip}>
          Skip for now
        </button>
      </span>
    </>
  );
}

/**
 * The search on No summary and No cover (Kristi, Oct 30; like today's Fill in details): searches
 * with the title and author, shows each result's cover or the start of its description, and
 * saves the one you pick the blanks-only way, with its source and one Undo.
 */
function FindOne({ it, author, busy, run, finish }: { it: LookupItem; author: string; busy: boolean; run: (it: LookupItem, f: () => Promise<void>) => Promise<void>; finish: Finish }) {
  const [typedWords, setWords] = useState<string | null>(null);
  const words = typedWords ?? `${it.book.title} ${author}`.trim();
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<Found[] | null>(null);
  const [error, setError] = useState('');
  const want = it.chip === 'summary' ? 'summary' : 'cover';

  const search = async () => {
    setError('');
    setSearching(true);
    try {
      const a = await searchFor(words.trim());
      let rs = a.results;
      // Open Library's search doesn't carry descriptions: read each one's work page.
      if (want === 'summary') {
        rs = await Promise.all(
          rs.map(async (r) => (r.summary || !r.openLibraryId ? r : { ...r, ...(await detailsFor(r.openLibraryId).then((d) => ({ summary: d.summary, summarySource: d.summarySource as Found['summarySource'] })).catch(() => ({}))) })),
        );
      }
      const has = rs.filter((r) => (want === 'summary' ? r.summary : r.coverUrl));
      setResults(has);
      if (!has.length) setError(rs.length ? `None of the ${rs.length} results has a ${want}. Try other words.` : (a.message ?? 'Nothing found. Try other words.'));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setSearching(false);
  };

  const use = (r: Found) =>
    run(it, async () => {
      const value = want === 'summary' ? r.summary! : r.coverUrl!;
      const source = (want === 'summary' ? r.summarySource ?? r.source : r.source) as 'Open Library' | 'Google Books';
      const rowId = want === 'summary' ? it.book.workId : await coverEdition(it.book.workId);
      if (!rowId) throw new Error('This book has no edition to put a cover on.');
      const res = await fillOne(it.book.id, it.row!, { table: want === 'summary' ? 'works' : 'editions', row_id: rowId, field: want === 'summary' ? 'summary' : 'cover_url', value, source });
      if (!res.filled) throw new Error(`It already has a ${want}.`);
      finish(it, `✓ ${want === 'summary' ? 'Summary' : 'Cover'} from ${source}`, () => undoPick(res.run), `${it.book.title}: ${want} from ${source}`);
    });

  return (
    <div class="lk-search lk-findbox" role="group" aria-label={`Find a ${want}`}>
      <div class="lk-inline">
        <input class="lk-input" value={words} aria-label={`Search for a ${want}`} onInput={(e) => setWords((e.target as HTMLInputElement).value)} onKeyDown={(e) => e.key === 'Enter' && void search()} />
        <button type="button" class="btn-main" disabled={searching || busy || !words.trim()} onClick={() => void search()}>
          {searching ? 'Searching…' : 'Search'}
        </button>
      </div>
      <span class="rw-quiet">Search Open Library and Google Books for {want === 'summary' ? 'a description' : 'a cover'}; change the words if nothing fits.</span>
      {error ? <span class="lk-error">{error}</span> : null}
      {results?.length ? (
        <div class="lk-cands">
          {results.map((r) => (
            <div key={r.key} class="lk-cand lk-found">
              {want === 'cover' ? <img class="lk-thumb" src={r.coverUrl!} alt="" loading="lazy" /> : null}
              <span class="lk-cand-text">
                <span>{r.title}</span>
                <span class="rw-quiet">{[r.authors.join(', '), r.year, r.source].filter(Boolean).join(' · ')}</span>
                {want === 'summary' ? <span class="lk-snippet">{r.summary!.length > 220 ? `${r.summary!.slice(0, 220)}…` : r.summary}</span> : null}
              </span>
              <button type="button" class="btn-secondary" disabled={busy} onClick={() => void use(r)}>
                {want === 'summary' ? 'Use this summary' : 'Use this cover'}
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
